import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Space, Tag, message, Card, Row, Col, Input, Switch, Modal, Form, Select, InputNumber, Popconfirm } from 'antd';
import { SearchOutlined, ReloadOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { getAdminList, createAdmin, updateAdmin, deleteAdmin, updatePassword } from '../services/admin';
import type { AdminInfo, CreateAdminParams } from '../services/admin';
import type { TablePaginationConfig } from 'antd/es/table/interface';

const { Option } = Select;

const AdminManagement: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [admins, setAdmins] = useState<AdminInfo[]>([]);
  const [searchText, setSearchText] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [modalVisible, setModalVisible] = useState(false);
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [passwordForm] = Form.useForm();

  // 获取管理员列表
  const fetchAdmins = async (page = 1, pageSize = 10, keyword = '') => {
    setLoading(true);
    try {
      const response = await getAdminList({ page, pageSize, keyword });
      console.log('Admin list response:', response);
      
      if (response && response.code === 0 && response.data) {
        console.log('Setting admins data:', response.data.list);
        setAdmins(response.data.list);
        setPagination(prev => ({
          ...prev,
          current: page,
          total: response.data.total
        }));
      } else {
        console.error('Invalid response format:', response);
        message.error(response?.message || '获取管理员列表失败');
      }
    } catch (error) {
      console.error('Error fetching admins:', error);
      message.error('获取管理员列表失败：网络错误');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('AdminManagement component mounted');
    fetchAdmins(pagination.current, pagination.pageSize);
  }, []);

  // 处理表格变化
  const handleTableChange = (newPagination: TablePaginationConfig) => {
    if (newPagination.current && newPagination.pageSize) {
      fetchAdmins(newPagination.current, newPagination.pageSize, searchText);
    }
  };

  // 处理搜索
  const handleSearch = () => {
    setPagination({ ...pagination, current: 1 });
    fetchAdmins(1, pagination.pageSize, searchText);
  };

  // 处理重置
  const handleReset = () => {
    setSearchText('');
    setPagination({ ...pagination, current: 1 });
    fetchAdmins(1, pagination.pageSize);
  };

  // 处理状态切换
  const handleStatusChange = async (id: number, status: number) => {
    try {
      const response = await updateAdmin({ id, status });
      if (response && response.code === 0) {
        message.success('管理员状态更新成功');
        fetchAdmins(pagination.current, pagination.pageSize, searchText);
      } else {
        message.error(response?.message || '更新管理员状态失败');
      }
    } catch (error) {
      console.error('更新管理员状态失败:', error);
      message.error('更新管理员状态失败，请稍后重试');
    }
  };

  // 处理添加
  const handleAdd = async (values: any) => {
    try {
      const response = await createAdmin(values);
      console.log('Add admin response:', response);
      if (response && response.code === 0) {
        message.success('添加管理员成功');
        setModalVisible(false);
        form.resetFields();
        fetchAdmins();
      } else {
        message.error(response?.message || '添加管理员失败');
      }
    } catch (error) {
      console.error('添加管理员失败:', error);
      message.error('添加管理员失败，请稍后重试');
    }
  };

  // 处理删除
  const handleDelete = async (id: number) => {
    try {
      const response = await deleteAdmin(id);
      if (response && response.code === 0) {
        message.success('删除管理员成功');
        fetchAdmins(pagination.current, pagination.pageSize, searchText);
      } else {
        message.error(response?.message || '删除管理员失败');
      }
    } catch (error) {
      console.error('删除管理员失败:', error);
      message.error('删除管理员失败，请稍后重试');
    }
  };

  // 处理密码修改
  const handlePasswordChange = async (values: any) => {
    try {
      const response = await updatePassword(values);
      if (response && response.code === 0) {
        message.success('密码修改成功');
        setPasswordModalVisible(false);
        passwordForm.resetFields();
      } else {
        message.error(response?.message || '密码修改失败');
      }
    } catch (error) {
      console.error('密码修改失败:', error);
      message.error('密码修改失败，请稍后重试');
    }
  };

  const columns: ColumnsType<AdminInfo> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username',
      width: 120,
    },
    {
      title: '等级',
      dataIndex: 'level_id',
      key: 'level_id',
      width: 100,
      render: (level) => (
        <Tag color={level === 1 ? 'blue' : 'green'}>
          {level === 1 ? '超级管理员' : '普通管理员'}
        </Tag>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status, record) => (
        <Switch
          checked={status === 1}
          onChange={(checked) => handleStatusChange(record.id, checked ? 1 : 0)}
        />
      ),
    },
    {
      title: '网站URL',
      dataIndex: 'weburl',
      key: 'weburl',
      ellipsis: true,
    },
    {
      title: '添加时间',
      dataIndex: 'addtime',
      key: 'addtime',
      width: 180,
      render: (addtime) => addtime ? new Date(addtime * 1000).toLocaleString() : '-',
    },
    {
      title: '最后登录时间',
      dataIndex: 'lasttime',
      key: 'lasttime',
      width: 180,
      render: (time) => time ? new Date(time * 1000).toLocaleString() : '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      render: (_, record) => (
        <Space size="middle">
          <Popconfirm
            title="确定要删除该管理员吗？"
            description="此操作不可恢复"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={24} style={{ textAlign: 'right' }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              form.resetFields();
              setModalVisible(true);
            }}
          >
            添加管理员
          </Button>
        </Col>
      </Row>
      <Table
        columns={columns}
        dataSource={admins}
        rowKey="id"
        loading={loading}
        scroll={{ x: 1200 }}
        size="small"
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `共 ${total} 条记录`,
        }}
        onChange={handleTableChange}
      />

      <Modal
        title="添加管理员"
        open={modalVisible}
        onOk={() => form.submit()}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        width={500}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleAdd}
          initialValues={{
            status: 1,
            level_id: 2
          }}
        >
          <Form.Item
            name="username"
            label="用户名"
            rules={[
              { required: true, message: '请输入用户名' },
              { min: 3, message: '用户名至少3个字符' }
            ]}
          >
            <Input placeholder="请输入用户名" />
          </Form.Item>

          <Form.Item
            name="password"
            label="密码"
            rules={[
              { required: true, message: '请输入密码' },
              { min: 6, message: '密码至少6个字符' }
            ]}
          >
            <Input.Password placeholder="请输入密码" />
          </Form.Item>

          <Form.Item
            name="level_id"
            label="管理员级别"
            rules={[{ required: true, message: '请选择管理员级别' }]}
          >
            <Select>
              <Option value={1}>超级管理员</Option>
              <Option value={2}>普通管理员</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="status"
            label="状态"
            rules={[{ required: true, message: '请选择状态' }]}
          >
            <Select>
              <Option value={1}>启用</Option>
              <Option value={0}>禁用</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="weburl"
            label="网站URL"
          >
            <Input placeholder="请输入网站URL（选填）" />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default AdminManagement; 